import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { UpdateCommissionsUiModel } from 'src/app/models/commissionsummary/update-commissions-ui.model';
import { ReviewCommissionsModel } from 'src/app/models/commission-summary/reviewcommissions.model';

@Injectable({
    providedIn: 'root'
})
export class ReviewCommissionsService {

    private host = environment.apiUrl + "reviewcommissions";
    private cachedHeaders: UpdateCommissionsUiModel[] | null = null;
    private searchData$?: Observable<ReviewCommissionsModel[]>;
    private repSupSearchDataCache = new Map<string, Observable<ReviewCommissionsModel[]>>();

    reviewCommissionsModel = new ReviewCommissionsModel();
    searchCriteriaSet = new Set<string>();
    searchDropdownCache: ReviewCommissionsModel[] = [];

    constructor(private http: HttpClient) {
    }

    static extractHttpErrorMessage(errorResponse: HttpErrorResponse, fallback = 'Request failed'): string {
        if (typeof errorResponse?.error === 'string') {
            return errorResponse.error;
        }
        if (errorResponse?.error?.message) {
            return errorResponse.error.message;
        }
        return errorResponse?.message || fallback;
    }

    /** Parse API / localStorage date values (ISO string, Date, or JSON.stringify-wrapped string). */
    static toDate(value: unknown): Date | null {
        if (value == null || value === '') {
            return null;
        }
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : value;
        }

        const raw = String(value).trim();
        if (!raw) {
            return null;
        }

        if (raw.startsWith('"')) {
            try {
                return ReviewCommissionsService.toDate(JSON.parse(raw));
            } catch {
                // fall through to direct parse
            }
        }

        const parsed = new Date(raw);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    static toStoredDateValue(value: unknown): string {
        const date = ReviewCommissionsService.toDate(value);
        return date ? date.toISOString() : '';
    }

    static isValidPayrollDateRange(validFromDt: Date | null, validToDt: Date | null): boolean {
        if (!validFromDt || !validToDt) {
            return false;
        }
        return validFromDt.getTime() <= validToDt.getTime();
    }

    resolvePayrollCalendarDates(
        calRunId: unknown,
        commissionPlanIds?: number[] | number | null
    ): { validFromDt: Date | null; validToDt: Date | null } {
        const empty = { validFromDt: null as Date | null, validToDt: null as Date | null };
        if (calRunId == null || calRunId === '') {
            return empty;
        }

        const cache = this.searchDropdownCache ?? [];
        if (!cache.length) {
            return empty;
        }

        const planIds = Array.isArray(commissionPlanIds)
            ? commissionPlanIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
            : commissionPlanIds != null
                ? [Number(commissionPlanIds)]
                : [];

        let match: ReviewCommissionsModel | undefined;
        if (planIds.length === 1) {
            match = cache.find(
                (item) => String(item.calRunId) === String(calRunId) && Number(item.commPlanId) === planIds[0]
            );
        }
        if (!match) {
            match = cache.find((item) => String(item.calRunId) === String(calRunId));
        }
        if (!match) {
            return empty;
        }

        return {
            validFromDt: ReviewCommissionsService.toDate(match.validFromDt),
            validToDt: ReviewCommissionsService.toDate(match.validToDt),
        };
    }

    setSearchDropdownCache(data: ReviewCommissionsModel[]): void {
        this.searchDropdownCache = data ?? [];
    }

    static isTopLevelPage(pageName: string): boolean {
        return pageName === 'ListUsers' || pageName === 'ListUsersByCustomer';
    }

    /** Drilldown pages whose SQL filters on :param_salesrepid / :param_account_sales_rep_id. */
    static requiresDrilldownSalesRepId(pageName: string | null | undefined): boolean {
        const page = (pageName || '').trim();
        return page === 'GetCorpHouseCustList'
            || page === 'GetCorpHouseCustByKpi'
            || page === 'GetMobileLinesByKpi';
    }

    static normalizeSalesRepId(value: unknown): string | null {
        if (value == null) {
            return null;
        }
        const normalized = String(value).trim();
        if (!normalized || normalized.toLowerCase() === 'null' || normalized.toUpperCase() === 'NA') {
            return null;
        }
        return normalized;
    }

    static isInvalidPageName(pageName: string | null | undefined): boolean {
        const normalized = (pageName || '').trim();
        return normalized === '' || normalized === 'No Page';
    }

    /** Mirrors backend ReviewCommissionsServiceImpl.getFinalPageName for FindByKpi drilldowns. */
    static resolveFindByKpiTargetPage(
        feedType: string | null | undefined,
        kpiType: string | null | undefined,
        gridName: string | null | undefined,
        commPlanPriority = 0
    ): string {
        if (commPlanPriority >= 20) {
            return 'GetSupList';
        }

        const feed = (feedType || '').trim();
        if (!feed || feed === 'No Page') {
            return 'No Page';
        }

        let effectiveKpiType = (kpiType || '').trim();
        if (gridName === 'Dependent Kpis' || gridName === 'Web Assist Dependent Kpis') {
            effectiveKpiType = 'NA';
        }

        if (effectiveKpiType === 'By KPI') {
            return 'GetDependentKpisList';
        }

        const feedNavigation: Record<string, string> = {
            'AVA': 'GetAVAList',
            'CAM Feed': 'GetCamFeedList',
            'CAM Feed Data Adds': 'GetCamFeedDataAdds',
            'CAM Feed Data Disc': 'GetCamFeedDataDisc',
            'CAM Feed Mob Add': 'GetCamFeedMobileAdd',
            'CAM Feed Mob Disc': 'GetCamFeedMobileDisc',
            'CAM Feed Video Adds': 'GetCamFeedVideoAdds',
            'CAM Feed Video Disc': 'GetCamFeedVideoDisc',
            'Call Details': 'GetCallList',
            'ENT Feed': 'GetEnterpriseList',
            'ENT Adj': 'FindByProductEntAdj',
            'ADJ INC': 'FindByProductEntAdj',
            'KOM Feed': 'FindByKpi',
            // Existing CommsUI page for Mobile feed List KPIs drilldown (may return duplicate rows until SQL join uses mob_feed_id).
            'Mobile': 'GetMobileList',
            // Legacy review-commissions.component.ts getPageNames(): Mobile 2 Plus + kpi_type NA.
            'Mobile 2 Plus': 'FindByProductMobileLinesPlus',
            // Legacy Review Commissions (review-commissions.component.ts) — not c_review_comms_navigations.
            'Fixed and Mobile': 'FindByProductDepntTotalPlus',
            'Survey Store Ratings': 'GetRetailList',
            'No Page': 'No Page',
        };

        return feedNavigation[feed] || 'No Page';
    }

    /** Internal Retail Agent Plan — scoped dispute row actions until CommsUI isdisputeview is set per grid. */
    static readonly INTERNAL_RETAIL_AGENT_PLAN_ID = 114;

    private static readonly DISPUTE_DETAIL_PAGE_NAMES = new Set([
        'FindByProductDepntTotalPlus',
        'FindByProductMobileLinesPlus',
    ]);

    static isPlanScopedDisputeDetailPage(
        pageName: string | null | undefined,
        commPlanId: number | null | undefined
    ): boolean {
        if (Number(commPlanId) !== ReviewCommissionsService.INTERNAL_RETAIL_AGENT_PLAN_ID) {
            return false;
        }
        const page = (pageName || '').trim();
        return ReviewCommissionsService.DISPUTE_DETAIL_PAGE_NAMES.has(page);
    }

    /**
     * Optional row-level hyperlink flag from ListKPIs SQL (column alias: ishyperlink only).
     * When absent, header.ishyperlinkview alone controls the link — no KPI ids in app code.
     * Note: Review Commissions Java maps all columns via ResultSet.getString(), so Postgres
     * booleans arrive as "t" / "f". Prefer SQL text 'true' / 'false' for clarity.
     * Do not read row.ishyperlinkview — that name collides with the header flag.
     */
    static isSqlRowHyperlinkAllowed(row: Record<string, unknown> | null | undefined): boolean {
        if (!row) {
            return true;
        }
        const rowFlag = ReviewCommissionsService.readRowPropertyIgnoreCase(row, 'ishyperlink');
        if (rowFlag === undefined || rowFlag === null || rowFlag === '') {
            return true;
        }
        if (rowFlag === true || rowFlag === 1) {
            return true;
        }
        if (rowFlag === false || rowFlag === 0) {
            return false;
        }
        const normalized = String(rowFlag).trim().toLowerCase();
        if (normalized === 't' || normalized === 'true' || normalized === 'y' || normalized === 'yes' || normalized === '1') {
            return true;
        }
        if (normalized === 'f' || normalized === 'false' || normalized === 'n' || normalized === 'no' || normalized === '0') {
            return false;
        }
        return true;
    }

    private static readRowPropertyIgnoreCase(row: Record<string, unknown>, name: string): unknown {
        if (Object.prototype.hasOwnProperty.call(row, name)) {
            return row[name];
        }
        const match = Object.keys(row).find((key) => key.toLowerCase() === name.toLowerCase());
        return match ? row[match] : undefined;
    }

    static isAdminRole(role: string | null | undefined): boolean {
        return (role || '').trim().toUpperCase() === 'ADMIN';
    }

    static isSalesRepresentativeRole(role: string | null | undefined): boolean {
        return (role || '').trim() === 'Sales Representative';
    }

    static isSupervisorRole(role: string | null | undefined): boolean {
        return (role || '').trim() === 'Supervisor';
    }

    static isSalesChannelAdminRole(role: string | null | undefined): boolean {
        return (role || '').trim() === 'Sales Channel Admin';
    }

    private static hasExplicitSearchValue(value: unknown): boolean {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (value == null) {
            return false;
        }
        const normalized = String(value).trim().toLowerCase();
        return normalized !== '' && normalized !== 'null';
    }

    /** Selected employee from a drilldown row (legacy searchEmpParm). */
    private static getDrilldownScEmpId(model: ReviewCommissionsModel): string | null {
        if (ReviewCommissionsService.hasExplicitSearchValue(model.scEmpID)) {
            return String(model.scEmpID).trim();
        }
        if (ReviewCommissionsService.hasExplicitSearchValue(model.sc_emp_id)) {
            return String(model.sc_emp_id).trim();
        }
        return null;
    }

    /**
     * Aligns Review Commissions New query scope with legacy Review Commissions:
     * admin = all employees (unless explicit search filters), rep/supervisor = own data.
     */
    static applyRoleBasedSearchScope(model: ReviewCommissionsModel): void {
        const loggedInRole = localStorage.getItem('CURRENT_USERROLE_SESSION');
        const loggedInEmpId = localStorage.getItem('CURRENT_EMPLOYEEID_SESSION');
        const loggedInScEmpId = localStorage.getItem('CURRENT_SC_EMP_ID_SESSION');

        model.loggedInEmpId = loggedInEmpId;
        model.user_role = loggedInRole;

        if (model.teamDataCheckedactive === true
            && ReviewCommissionsService.isTopLevelPage(model.pageName || '')) {
            model.user_role = 'SupervisorTeamData';
            model.employeeId = null;
            model.scEmpID = null;
            model.sc_emp_id = null;
            model.firstName = null;
            model.lastName = null;
            model.sales_rep_id = null;
            localStorage.setItem('level', '1');
            return;
        }

        if (ReviewCommissionsService.isSalesRepresentativeRole(loggedInRole)) {
            model.employeeId = loggedInEmpId;
            model.scEmpID = loggedInScEmpId;
            model.firstName = null;
            model.lastName = null;
            model.sales_rep_id = null;
            return;
        }

        if (ReviewCommissionsService.isSupervisorRole(loggedInRole)
            || ReviewCommissionsService.isSalesChannelAdminRole(loggedInRole)) {
            if (model.teamDataCheckedactive !== true) {
                model.scEmpID = null;
                model.sc_emp_id = null;
                model.dependentScempid = null;
            }

            const drilldownScEmpId = ReviewCommissionsService.getDrilldownScEmpId(model);
            if (model.teamDataCheckedactive === true
                && !ReviewCommissionsService.isTopLevelPage(model.pageName || '')
                && drilldownScEmpId) {
                // Team drilldown: keep clicked employee (legacy searchEmpParm), not supervisor login.
                model.user_role = loggedInRole;
                model.scEmpID = drilldownScEmpId;
                model.sc_emp_id = drilldownScEmpId;
                model.employeeId = null;
                model.firstName = null;
                model.lastName = null;
                localStorage.setItem('level', '1');
                return;
            }

            if (ReviewCommissionsService.isSupervisorRole(loggedInRole)) {
                localStorage.setItem('level', '0');
            }

            model.employeeId = loggedInEmpId;
            model.scEmpID = loggedInScEmpId;
            model.firstName = null;
            model.lastName = null;
            model.sales_rep_id = null;
            return;
        }

        if (ReviewCommissionsService.isAdminRole(loggedInRole)) {
            if (!ReviewCommissionsService.hasExplicitSearchValue(model.firstName)) {
                model.firstName = null;
            }
            if (!ReviewCommissionsService.hasExplicitSearchValue(model.lastName)) {
                model.lastName = null;
            }
            if (!ReviewCommissionsService.hasExplicitSearchValue(model.employeeId)) {
                model.employeeId = null;
            }
            if (!ReviewCommissionsService.hasExplicitSearchValue(model.scEmpID)) {
                model.scEmpID = null;
            }
            if (!ReviewCommissionsService.hasExplicitSearchValue(model.sales_rep_id)) {
                model.sales_rep_id = null;
            }
        }
    }

    /** Mirrors legacy Review Commissions getDisputeStatus date comparison rules. */
    static persistDisputeCloseStatus(
        disputeCloseData: Array<{ dispute_close_dt?: Date | string | null }> | null | undefined
    ): void {
        const storageKey = 'SESSION_DISPUTE_CLOSE_STATUS';

        if (!disputeCloseData?.length || !disputeCloseData[0]?.dispute_close_dt) {
            localStorage.setItem(storageKey, 'valid');
            return;
        }

        const today = new Date();
        const closeDate = new Date(disputeCloseData[0].dispute_close_dt);
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const closeDateOnly = new Date(closeDate.getFullYear(), closeDate.getMonth(), closeDate.getDate());

        if (todayOnly.getTime() <= closeDateOnly.getTime()) {
            localStorage.setItem(storageKey, 'valid');
        } else {
            localStorage.setItem(storageKey, 'notvalid');
        }
    }

    prepareNewSearchSession(
        model: ReviewCommissionsModel,
        pageName: string,
        headerInfo: string,
        searchCriteriaSet: Set<string>
    ): void {
        localStorage.setItem('SESSION_SC', model.salesChannel ?? '');
        localStorage.setItem('country', model.country ?? '');
        localStorage.setItem('SESSION_COMMPLAN', JSON.stringify(model.commissionPlanId));
        localStorage.setItem('SESSION_CALRUNID', model.calRunId ?? '');
        localStorage.setItem('SESSION_FNAME', model.firstName ?? '');
        localStorage.setItem('SESSION_LNAME', model.lastName ?? '');
        localStorage.setItem('SESSION_EMPID', model.employeeId ?? '');
        localStorage.setItem('SESSION_COMPASID', model.scEmpID ?? '');
        localStorage.setItem('SESSION_REPID', model.sales_rep_id ?? '');

        const validFromValue = ReviewCommissionsService.toStoredDateValue(model.validFromDt);
        const validToValue = ReviewCommissionsService.toStoredDateValue(model.validToDt);
        if (validFromValue) {
            localStorage.setItem('validFromDt', validFromValue);
        }
        if (validToValue) {
            localStorage.setItem('validToDt', validToValue);
        }
        if (model.effectiveDate) {
            localStorage.setItem('SESSION_EFFDATE', String(model.effectiveDate));
        }

        model.pageName = pageName;
        model.selectedCommPlanHeader = headerInfo;

        localStorage.setItem('SESSION_RC_SEARCH_MODEL', JSON.stringify(model));
        this.clearDrilldownSession();
        localStorage.setItem('SESSION_reviewCommissionsModel', JSON.stringify(model));
        localStorage.setItem('SESSION_RC_PAGE_NAME', pageName);
        localStorage.setItem('SESSION_RC_HEADER_INFO', headerInfo);
        if (ReviewCommissionsService.isTopLevelPage(pageName)) {
            localStorage.setItem('SESSION_RC_ROOT_LIST_PAGE', pageName);
        }

        this.reviewCommissionsModel = model;
        this.searchCriteriaSet = searchCriteriaSet;
    }

    public saveSearchModel(model: ReviewCommissionsModel): void {
        localStorage.setItem('SESSION_RC_SEARCH_MODEL', JSON.stringify(model));
    }

    public clearDrilldownSession(): void {
        localStorage.removeItem('SESSION_reviewCommissionsModel');
        localStorage.removeItem('SESSION_RC_CLICK_CONTEXT');
        localStorage.removeItem('SESSION_RC_MAIN_RESPONSE');
        localStorage.removeItem('SESSION_RC_CURRENT_PAGE_INDEX');
        localStorage.removeItem('SESSION_RC_NAV_FRAMES');
        localStorage.removeItem('SESSION_RC_RESOLVED_PAGE_NAME');
        localStorage.removeItem('SESSION_RC_ROOT_LIST_PAGE');
    }

    clearSessionStorage(): void {
        localStorage.removeItem('SESSION_RC_SEARCH_MODEL');
        localStorage.removeItem('SESSION_reviewCommissionsModel');
        localStorage.removeItem('SESSION_RC_PAGE_NAME');
        localStorage.removeItem('SESSION_RC_HEADER_INFO');
        localStorage.removeItem('SESSION_RC_CLICK_CONTEXT');
        localStorage.removeItem('SESSION_RC_MAIN_RESPONSE');
        localStorage.removeItem('SESSION_RC_CURRENT_PAGE_INDEX');
        localStorage.removeItem('SESSION_RC_NAV_FRAMES');
        localStorage.removeItem('SESSION_RC_RESOLVED_PAGE_NAME');
        localStorage.removeItem('SESSION_RC_ROOT_LIST_PAGE');
        localStorage.removeItem('SESSION_SC');
        localStorage.removeItem('SESSION_COMMPLAN');
        localStorage.removeItem('SESSION_CALRUNID');
        localStorage.removeItem('SESSION_FNAME');
        localStorage.removeItem('SESSION_LNAME');
        localStorage.removeItem('SESSION_EMPID');
        localStorage.removeItem('SESSION_COMPASID');
        localStorage.removeItem('SESSION_REPID');
        localStorage.removeItem('SESSION_EFFDATE');
        localStorage.removeItem('validFromDt');
        localStorage.removeItem('validToDt');
        localStorage.removeItem('bycust_feed');
        localStorage.removeItem('acc2');
        localStorage.removeItem('subno2');
    }

    public runQuery(model: ReviewCommissionsModel): Observable<any> {
        const payload = JSON.parse(JSON.stringify(model ?? {})) as Record<string, unknown>;
        let pageName = (
            model?.pageName
            || payload['pageName']
            || localStorage.getItem('SESSION_RC_PAGE_NAME')
            || ''
        ).toString().trim();
        const resolvedPageName = localStorage.getItem('SESSION_RC_RESOLVED_PAGE_NAME')?.trim();
        if (pageName === 'FindByKpi'
            && resolvedPageName
            && resolvedPageName !== 'FindByKpi'
            && !ReviewCommissionsService.isInvalidPageName(resolvedPageName)) {
            pageName = resolvedPageName;
        }
        if (ReviewCommissionsService.isInvalidPageName(pageName)) {
            return throwError(() => new Error('No Drill Downs Allocated'));
        }
        payload['pageName'] = pageName;
        payload['grid_name'] = ReviewCommissionsService.isTopLevelPage(pageName) ? null : payload['grid_name'];
        if (ReviewCommissionsService.isTopLevelPage(pageName)) {
            payload['kpi_id'] = 0;
            payload['dpndnt_kpi_id'] = 0;
            payload['dependentKpiId'] = 0;
        }
        if (payload['dependentKpiId'] != null && payload['dependentKpiId'] !== 0
            && (payload['dpndnt_kpi_id'] == null || payload['dpndnt_kpi_id'] === 0)) {
            payload['dpndnt_kpi_id'] = payload['dependentKpiId'];
        }
        return this.http.put<any>(`${this.host}/getrunquerydata`, payload);
    }

    public getSearchData(): Observable<ReviewCommissionsModel[]> {
        if (!this.searchData$) {
            this.searchData$ = this.http.get<ReviewCommissionsModel[]>(`${this.host}/getsearchdata`).pipe(shareReplay(1));
        }
        return this.searchData$;
    }

    public getRepSupSearchData(empid: string): Observable<ReviewCommissionsModel[]> {
        const key = (empid || '').trim();
        if (!this.repSupSearchDataCache.has(key)) {
            this.repSupSearchDataCache.set(
                key,
                this.http.get<ReviewCommissionsModel[]>(`${this.host}/getrepsearchdata/` + key).pipe(shareReplay(1))
            );
        }
        return this.repSupSearchDataCache.get(key)!;
    }

    public fetchHeaders(): Observable<UpdateCommissionsUiModel[]> {
        if (this.cachedHeaders) {
            return of(this.cachedHeaders);
        }
        return this.http.get<UpdateCommissionsUiModel[]>(`${this.host}/getheaders`).pipe(
            tap((headers) => {
                this.cachedHeaders = headers;
            })
        );
    }

    public getCommReportData(reviewCommissionsModel: ReviewCommissionsModel): Observable<any[]> {
        return this.http.put<any[]>(`${this.host}/getcommreportdata`, reviewCommissionsModel);
    }

    public sendForApproval(reviewCommissionsModel: ReviewCommissionsModel): Observable<any> {
        return this.http.put<any>(`${this.host}/sendforapproval`, reviewCommissionsModel);
    }
}
